<template>
  <div class="track-draw" v-loading="loading">
    <div class="demo-layout">
      <!-- ===== 左侧：地图 ===== -->
      <section class="map-panel">
        <div class="map-panel__toolbar">
          <div class="toolbar-copy">
            <h3>轨迹绘制</h3>
          </div>
          <div class="toolbar-actions">
            <el-button
              size="small"
              :type="drawing ? 'danger' : 'primary'"
              icon="el-icon-edit-outline"
              @click="toggleDraw"
            >
              {{ drawing ? '结束绘制' : '开始绘制' }}
            </el-button>
            <el-button size="small" icon="el-icon-delete" :disabled="points.length === 0" @click="clearAll">
              清空
            </el-button>
            <el-button size="small" icon="el-icon-rank" :disabled="points.length === 0" @click="fitView">
              自适应
            </el-button>
            <el-button size="small" icon="el-icon-location-outline" @click="locateCurrentPosition">
              定位
            </el-button>
          </div>
        </div>

        <div class="map-stage-wrap">
          <div ref="mapContainer" class="map-stage"></div>

          <!-- 绘制中的引导浮层 -->
          <div class="draw-tip" :class="{ 'is-active': drawing }">
            <i class="el-icon-mouse"></i>
            <span v-if="drawing">绘制中：点击地图追加轨迹点，双击结束绘制</span>
            <span v-else>点击「开始绘制」后在地图上落点</span>
          </div>
        </div>

        <div class="map-legend">
          <div class="legend-item">
            <span class="legend-line"></span>
            <span>轨迹线路（{{ points.length }} 个点）</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot start"></span>
            <span>起点</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot end"></span>
            <span>终点</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot normal"></span>
            <span>途经点</span>
          </div>
          <div class="legend-item legend-tip">
            <i class="el-icon-info"></i>
            <span>点击线上任意位置撤销最后一点，点击标记删除该点</span>
          </div>
        </div>
      </section>

      <!-- ===== 右侧：数据面板 ===== -->
      <aside class="side-panel">
        <div class="panel-card stats-card">
          <div class="panel-card__title">绘制概览</div>
          <div class="stats-grid">
            <div class="stats-item">
              <span>轨迹点数</span>
              <strong>{{ points.length }}</strong>
            </div>
            <div class="stats-item">
              <span>总里程</span>
              <strong class="distance">{{ totalDistanceText }}</strong>
            </div>
          </div>
          <div class="stats-line">
            <span>绘制状态</span>
            <el-tag size="mini" :type="drawing ? 'danger' : 'info'" effect="plain">
              {{ drawing ? '绘制中' : '待绘制' }}
            </el-tag>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-card__title">
            轨迹数据
            <div class="title-actions">
              <el-radio-group v-model="dataFormat" size="mini">
                <el-radio-button label="json">JSON</el-radio-button>
                <el-radio-button label="csv">CSV</el-radio-button>
                <el-radio-button label="wkt">WKT</el-radio-button>
              </el-radio-group>
            </div>
          </div>

          <div class="data-toolbar">
            <el-button size="mini" type="primary" icon="el-icon-document-copy" :disabled="points.length === 0" @click="copyTrackData">
              复制数据
            </el-button>
            <el-button size="mini" icon="el-icon-download" :disabled="points.length === 0" @click="downloadTrackData">
              下载
            </el-button>
          </div>

          <el-input
            ref="dataTextarea"
            type="textarea"
            :rows="10"
            readonly
            resize="none"
            class="data-textarea"
            :value="trackDataText"
            placeholder="暂无轨迹数据，请先在地图上绘制"
          />
        </div>

        <div class="panel-card">
          <div class="panel-card__title">
            轨迹点列表
            <span class="point-count">{{ points.length }} 个</span>
          </div>
          <div v-if="points.length === 0" class="empty-tip">尚未绘制任何轨迹点</div>
          <div v-else class="point-list">
            <div v-for="(point, index) in points" :key="point.id" class="point-item">
              <span class="point-index" :class="pointTypeClass(index)">{{ index + 1 }}</span>
              <span class="point-coord">{{ formatCoordinate(point) }}</span>
              <span class="point-action" title="复制该点坐标" @click="copyPoint(point)">
                <i class="el-icon-document-copy"></i>
              </span>
              <span class="point-action is-danger" title="删除该点" @click="removePoint(index)">
                <i class="el-icon-close"></i>
              </span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

const AMAP_KEY = '0826878f934aa6f773237d9c82dfcf45'
const DEFAULT_CENTER = [116.397428, 39.90923]
const DEFAULT_ZOOM = 13

/**
 * 判断坐标是否合法（经度 [-180,180]，纬度 [-90,90]）。
 * @param {Array} position [lng, lat]
 * @returns {boolean}
 */
function isValidPosition(position) {
  if (!Array.isArray(position) || position.length < 2) {
    return false
  }
  const lng = Number(position[0])
  const lat = Number(position[1])
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false
  }
  return Math.abs(lng) <= 180 && Math.abs(lat) <= 90
}

export default {
  name: 'TrackDraw',
  data() {
    return {
      loading: false,
      AMap: null,
      map: null,
      markerLayer: null,
      drawing: false,
      points: [],
      pointSeq: 0,
      dataFormat: 'json'
    }
  },
  computed: {
    // 当前格式下的轨迹数据文本，同时用于「复制」和「下载」。
    trackDataText() {
      if (this.points.length === 0) {
        return ''
      }
      if (this.dataFormat === 'csv') {
        return this.buildCsvText()
      }
      if (this.dataFormat === 'wkt') {
        return this.buildWktText()
      }
      return this.buildJsonText()
    },
    // 轨迹总里程（公里），保留三位小数。
    totalDistance() {
      let meters = 0
      for (let index = 1; index < this.points.length; index += 1) {
        meters += this.calculateDistance(this.points[index - 1], this.points[index])
      }
      return meters
    },
    totalDistanceText() {
      if (this.points.length < 2) {
        return '0 m'
      }
      if (this.totalDistance < 1000) {
        return `${this.totalDistance.toFixed(1)} m`
      }
      return `${(this.totalDistance / 1000).toFixed(3)} km`
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.off('click', this.handleMapClick)
      this.map.off('dblclick', this.handleMapDblClick)
      this.map.destroy()
      this.map = null
    }
  },
  methods: {
    /* ==================== 地图初始化 ==================== */
    async initMap() {
      this.loading = true
      try {
        const AMap = await AMapLoader.load({
          key: AMAP_KEY,
          version: '2.0',
          plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.Geocoder']
        })

        this.AMap = AMap
        this.map = new AMap.Map(this.$refs.mapContainer, {
          zoom: DEFAULT_ZOOM,
          center: DEFAULT_CENTER,
          resizeEnable: true,
          viewMode: '2D',
          doubleClickZoom: false // 双击用于结束绘制，关闭双击放大
        })

        this.map.addControl(new AMap.Scale())
        this.map.addControl(new AMap.ToolBar({ position: 'RB' }))

        // 图层组：轨迹线 + 轨迹点统一管理，清空时只清图层不动底图
        this.markerLayer = new AMap.OverlayGroup()
        this.map.add(this.markerLayer)

        this.map.on('click', this.handleMapClick)
        this.map.on('dblclick', this.handleMapDblClick)
      } catch (error) {
        console.error('Failed to init AMap:', error)
        this.$toast.error('高德地图加载失败，请检查网络或 Key 配置')
      } finally {
        this.loading = false
      }
    },

    /* ==================== 绘制交互 ==================== */
    // 开始 / 结束绘制。
    toggleDraw() {
      if (!this.map) {
        this.$toast.warning('地图尚未初始化完成')
        return
      }
      this.drawing = !this.drawing
      this.map.setDefaultCursor(this.drawing ? 'crosshair' : 'default')
      this.$toast.success(this.drawing ? '已进入绘制模式，请在地图上点击落点' : '已结束绘制')
    },

    // 地图单击：绘制中追加轨迹点。
    handleMapClick(event) {
      if (!this.drawing) {
        return
      }
      const position = [event.lnglat.getLng(), event.lnglat.getLat()]
      if (!isValidPosition(position)) {
        return
      }
      this.appendPoint(position)
    },

    // 地图双击：结束绘制。
    handleMapDblClick() {
      if (!this.drawing) {
        return
      }
      this.drawing = false
      if (this.map) {
        this.map.setDefaultCursor('default')
      }
      this.$toast.success('绘制完成，可在右侧查看并复制轨迹数据')
    },

    // 追加一个轨迹点并重绘。
    appendPoint(position) {
      this.pointSeq += 1
      this.points.push({
        id: `track_point_${this.pointSeq}`,
        lng: Number(position[0].toFixed(6)),
        lat: Number(position[1].toFixed(6))
      })
      this.rendering = true
      this.renderTrack()
      this.$nextTick(() => {
        this.scrollPointListToBottom()
      })
    },

    // 删除指定轨迹点。
    removePoint(index) {
      this.points.splice(index, 1)
      this.renderTrack()
    },

    // 撤销最后一个轨迹点。
    removeLastPoint() {
      if (this.points.length === 0) {
        return
      }
      this.points.pop()
      this.renderTrack()
      this.$toast.success('已撤销最后一个轨迹点')
    },

    // 清空全部轨迹。
    clearAll() {
      this.points = []
      this.pointSeq = 0
      this.drawing = false
      if (this.map) {
        this.map.setDefaultCursor('default')
      }
      this.renderTrack()
      this.$toast.success('已清空轨迹')
    },

    /* ==================== 地图渲染 ==================== */
    // 重新渲染轨迹线、途经点、起终点标记。
    renderTrack() {
      if (!this.AMap || !this.map || !this.markerLayer) {
        return
      }

      this.markerLayer.clearOverlays()

      const path = this.points.map(point => [point.lng, point.lat])

      if (path.length >= 2) {
        const line = new this.AMap.Polyline({
          path,
          strokeColor: '#3478f6',
          strokeWeight: 6,
          strokeOpacity: 0.9,
          lineJoin: 'round',
          showDir: true,
          zIndex: 60
        })
        // 点击轨迹线任意位置 = 撤销最后一个点（绘制时快速纠错）
        line.on('click', () => this.removeLastPoint())
        this.markerLayer.addOverlay(line)
      }

      this.points.forEach((point, index) => {
        const isStart = index === 0
        const isEnd = index === this.points.length - 1 && this.points.length > 1
        const marker = new this.AMap.Marker({
          position: [point.lng, point.lat],
          content: this.buildMarkerHtml(index + 1, isStart, isEnd),
          offset: new this.AMap.Pixel(-11, -11),
          zIndex: isStart || isEnd ? 100 : 80,
          extData: { trackIndex: index }
        })

        // 点击标记 = 删除该点（便于绘制时纠错）
        marker.on('click', () => this.removePoint(index))
        this.markerLayer.addOverlay(marker)
      })
    },

    // 轨迹点标记 HTML：起点绿、终点红、途经点蓝。
    buildMarkerHtml(label, isStart, isEnd) {
      let background = '#3478f6'
      if (isStart) {
        background = '#4caf50'
      } else if (isEnd) {
        background = '#f56c6c'
      }

      return `<div style="
        width: 22px;
        height: 22px;
        line-height: 22px;
        text-align: center;
        border-radius: 50%;
        background: ${background};
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        border: 2px solid rgba(255,255,255,0.95);
        box-shadow: 0 3px 8px rgba(0,0,0,0.25);
        cursor: pointer;
      ">${label}</div>`
    },

    // 视图自适应到整条轨迹。
    fitView() {
      if (!this.map || this.points.length === 0) {
        return
      }
      if (this.points.length === 1) {
        this.map.setCenter([this.points[0].lng, this.points[0].lat])
        this.map.setZoom(16)
        return
      }
      this.map.setFitView(null, false, [80, 80, 80, 80])
    },

    // 定位到当前位置。
    locateCurrentPosition() {
      if (!this.map) {
        return
      }
      this.map.plugin('AMap.Geolocation', () => {
        const geolocation = new this.AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: 8000,
          zoomToAccuracy: true
        })
        this.map.addControl(geolocation)
        geolocation.getCurrentPosition((status, result) => {
          if (status !== 'complete') {
            this.$toast.warning('定位失败，请检查浏览器定位权限')
          }
        })
      })
    },

    /* ==================== 数据格式化 ==================== */
    // JSON：结构化轨迹数据，包含点序号、经纬度与里程。
    buildJsonText() {
      const payload = {
        type: 'Polyline',
        coordinateSystem: 'GCJ02',
        pointCount: this.points.length,
        totalDistanceMeter: Number(this.totalDistance.toFixed(2)),
        points: this.points.map((point, index) => ({
          index: index + 1,
          lng: point.lng,
          lat: point.lat
        }))
      }
      return JSON.stringify(payload, null, 2)
    },

    // CSV：序号,经度,纬度
    buildCsvText() {
      const rows = ['index,lng,lat']
      this.points.forEach((point, index) => {
        rows.push(`${index + 1},${point.lng},${point.lat}`)
      })
      return rows.join('\n')
    },

    // WKT：LINESTRING 线要素
    buildWktText() {
      const coords = this.points.map(point => `${point.lng} ${point.lat}`).join(', ')
      return `LINESTRING (${coords})`
    },

    // 单点展示文案。
    formatCoordinate(point) {
      return `${point.lng}, ${point.lat}`
    },

    // 列表里点位类型样式。
    pointTypeClass(index) {
      if (index === 0) {
        return 'is-start'
      }
      if (index === this.points.length - 1 && this.points.length > 1) {
        return 'is-end'
      }
      return ''
    },

    // 复制单个轨迹点坐标。
    async copyPoint(point) {
      const text = `${point.lng},${point.lat}`
      const ok = await this.copyText(text)
      if (ok) {
        this.$toast.success('已复制该点坐标')
      }
    },

    // 复制全量轨迹数据（按当前格式）。
    async copyTrackData() {
      if (this.points.length === 0) {
        return
      }
      const ok = await this.copyText(this.trackDataText)
      if (ok) {
        this.$toast.success(`已复制 ${this.points.length} 个轨迹点数据（${this.dataFormat.toUpperCase()}）`)
        return
      }
      // 剪贴板不可用时，选中文本域内容让用户手动 Ctrl+C
      const textarea = this.$refs.dataTextarea && this.$refs.dataTextarea.$refs
        ? this.$refs.dataTextarea.$refs.textarea
        : null
      if (textarea) {
        textarea.select()
      }
      this.$toast.warning('当前环境不支持自动复制，已为你选中文本，请按 Ctrl+C 复制')
    },

    // 下载轨迹数据文件。
    downloadTrackData() {
      if (this.points.length === 0) {
        return
      }
      const extensionMap = { json: 'json', csv: 'csv', wkt: 'txt' }
      const extension = extensionMap[this.dataFormat] || 'txt'
      const blob = new Blob([this.trackDataText], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `track_${Date.now()}.${extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$toast.success('轨迹数据已下载')
    },

    /**
     * 通用复制：优先 navigator.clipboard，兜底 execCommand。
     * @param {string} text 待复制文本
     * @returns {Promise<boolean>} 是否复制成功
     */
    async copyText(text) {
      if (!text) {
        return false
      }
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text)
          return true
        } catch (error) {
          console.warn('clipboard API failed, fallback to execCommand:', error)
        }
      }
      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.top = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(textarea)
        return ok
      } catch (error) {
        console.error('copy failed:', error)
        return false
      }
    },

    /* ==================== 工具方法 ==================== */
    // 两点球面距离（米），Haversine 公式。
    calculateDistance(from, to) {
      const earthRadius = 6371008.8
      const radLat1 = (from.lat * Math.PI) / 180
      const radLat2 = (to.lat * Math.PI) / 180
      const deltaLat = radLat2 - radLat1
      const deltaLng = ((to.lng - from.lng) * Math.PI) / 180
      const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
      return 2 * earthRadius * Math.asin(Math.sqrt(a))
    },

    // 点位列表滚动到底部（新增点时）。
    scrollPointListToBottom() {
      const list = this.$el.querySelector('.point-list')
      if (list) {
        list.scrollTop = list.scrollHeight
      }
    }
  }
}
</script>

<style scoped lang="scss">
.track-draw {
  width: 100%;
}

.demo-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 18px;
  min-height: 680px;
}

.map-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.map-panel__toolbar,
.panel-card {
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 16px;
  background: var(--surface-1);
  box-shadow: 0 10px 28px rgba(31, 45, 61, 0.08);
}

.map-panel__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #f9fbff 0%, #eef4ff 100%);
}

.toolbar-copy {
  min-width: 0;

  h3 {
    margin: 0 0 8px;
    color: #22324a;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #6d7a8a;
    font-size: 13px;
    line-height: 1.7;
  }
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.map-stage-wrap {
  position: relative;
  flex: 1;
  min-height: 480px;
}

.map-stage {
  width: 100%;
  height: 100%;
  min-height: 480px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(31, 45, 61, 0.06);
  box-shadow: 0 10px 28px rgba(31, 45, 61, 0.08);
}

.draw-tip {
  position: absolute;
  left: 50%;
  top: 16px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(31, 45, 61, 0.82);
  color: #fff;
  font-size: 13px;
  pointer-events: none;
  transition: background 0.2s ease;

  &.is-active {
    background: rgba(52, 120, 246, 0.92);
  }
}

.map-legend {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 14px;
  background: var(--surface-1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5f6b7a;
  font-size: 13px;
}

.legend-tip {
  color: #8b97a8;

  i {
    font-size: 14px;
  }
}

.legend-line {
  width: 26px;
  height: 0;
  border-top: 3px solid #3478f6;
  display: inline-block;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;

  &.start {
    background: #4caf50;
  }

  &.end {
    background: #f56c6c;
  }

  &.normal {
    background: #3478f6;
  }
}

.side-panel {
  width: 400px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
}

.panel-card {
  padding: 16px 18px;
}

.panel-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 600;
}

.title-actions {
  flex-shrink: 0;
}

.point-count {
  color: #8b97a8;
  font-size: 12px;
  font-weight: 400;
}

.stats-card {
  padding-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.stats-item {
  padding: 14px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
  border: 1px solid rgba(31, 45, 61, 0.06);

  span {
    display: block;
    margin-bottom: 8px;
    color: #6b778c;
    font-size: 12px;
  }

  strong {
    color: #1f2d3d;
    font-size: 22px;
    line-height: 1;
  }

  .distance {
    font-size: 18px;
    color: #3478f6;
  }
}

.stats-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  color: #6b778c;
  font-size: 13px;
}

.data-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.data-textarea {
  :deep(.el-textarea__inner) {
    font-family: Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 1.7;
    background: var(--surface-1);
    color: #303133;
  }
}

.empty-tip {
  padding: 20px 0;
  text-align: center;
  color: #a3adba;
  font-size: 13px;
}

.point-list {
  max-height: 220px;
  overflow: auto;
}

.point-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  color: #445165;

  &:hover {
    background: var(--surface-2);
  }
}

.point-index {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  background: #3478f6;
  color: #fff;
  font-size: 11px;
  font-weight: 600;

  &.is-start {
    background: #4caf50;
  }

  &.is-end {
    background: #f56c6c;
  }
}

.point-coord {
  flex: 1;
  min-width: 0;
  font-family: Menlo, Consolas, monospace;
  color: #303133;
}

.point-action {
  flex-shrink: 0;
  cursor: pointer;
  color: #8b97a8;

  &:hover {
    color: #3478f6;
  }

  &.is-danger:hover {
    color: #f56c6c;
  }
}

:deep(.amap-marker) {
  cursor: pointer;
}

@media (max-width: 1400px) {
  .demo-layout {
    grid-template-columns: minmax(0, 1fr) 340px;
  }

  .side-panel {
    width: 340px;
  }
}

@media (max-width: 1200px) {
  .demo-layout {
    display: flex;
    flex-direction: column;
  }

  .side-panel {
    width: 100%;
  }
}
</style>
